<!--
  HelperText — the caption under a Zone form field. INTERNAL to the fields in `zone/components/base/`.

  Not a public Zone component and deliberately not listed in llms.txt: every field (Select, TextInput,
  TextArea, Toggle, Checkbox, DayPicker, DatePicker, PhoneField) renders the same caption and used to
  carry its own copy, which drifted (gray-500 here, gray-600 there, the icon on the first line or
  centred). This is the one copy, Figma's "Helper text wrapper": an 18px icon and a 16px caption
  line, 4px apart, the icon on the FIRST line when the caption wraps (`items-start`, which Figma's
  error copy explicitly allows for). `leading-4` shrinks the icon's line box to the caption's, so the
  glyph centres on the text instead of sitting ~5px low under ZoneIcon's default `text-lg` line box.

  The owner keeps `helperText`, `warning`, `#helper`, `hasHelper` and `helperId` (its field's
  `aria-describedby` points at `id`), and renders this as
  `<HelperText v-if="hasHelper" :id="helperId" :invalid="invalid" :warning="warning"><slot
  name="helper">{{ helperText }}</slot></HelperText>`. `invalid` wins over `warning`.
-->

<template>
  <div
    class="flex items-start gap-1"
    data-testid="helper-row"
  >
    <ZoneIcon
      v-if="invalid"
      data-testid="helper-icon"
      icon="error"
      color="red"
      :shade="600"
      class="shrink-0 leading-4"
    />
    <ZoneIcon
      v-else-if="warning"
      data-testid="helper-icon"
      icon="warning"
      color="yellow"
      :shade="600"
      class="shrink-0 leading-4"
    />
    <span
      :id="id"
      data-testid="helper"
      :class="textClasses"
    >
      <slot />
    </span>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import ZoneIcon from '../Icon.vue';

defineOptions({ name: 'ZoneFieldHelperText' });

const props = defineProps({
  /** The caption's id, for the field's `aria-describedby`. */
  id: {
    type: String,
    default: null,
  },
  /** Red caption with the error icon. Wins over `warning`. */
  invalid: {
    type: Boolean,
    default: false,
  },
  /** Yellow caption with the warning icon. */
  warning: {
    type: Boolean,
    default: false,
  },
  /** One line, ellipsis: for a caption that shares its row (TextArea's counter). */
  truncate: {
    type: Boolean,
    default: false,
  },
});

defineSlots<{
  /** The caption. */
  default?(): any;
}>();

const textClasses = computed<string>(() => {
  const classes = ['min-w-0 flex-1 text-xs leading-4'];

  if (props.invalid) {
    classes.push('text-red-600');
  } else if (props.warning) {
    classes.push('text-yellow-600');
  } else {
    classes.push('text-gray-600');
  }

  if (props.truncate) {
    classes.push('truncate');
  }

  return classes.join(' ');
});
</script>
